import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarSync, Copy } from 'lucide-react';
import { useState } from 'react';
import { profileApi } from '@/api/profiles';
import { ConfirmDialog } from './ConfirmDialog';
import { Dialog } from './ui';

const STEPS: [string, string][] = [
  ['Google Календарь', 'Слева «Другие календари» → «+» → «Из URL», вставьте ссылку https (не webcal). Обновляется раз в несколько часов.'],
  ['Apple Календарь', 'Файл → «Новая подписка на календарь…», вставьте ссылку webcal или нажмите «Открыть в календаре».'],
  ['Яндекс Календарь', 'Слева «Добавить календарь» → «Подписаться по ссылке», вставьте ссылку https.'],
  ['Outlook', 'Добавить календарь → «Подписаться из интернета», вставьте ссылку https.'],
];

/** «Подписаться на календарь»: a personal secret link to every meeting the person can see. */
export function CalendarSubscribe() {
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [copied, setCopied] = useState<'https' | 'webcal' | null>(null);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['me', 'calendar'], queryFn: profileApi.calendar, enabled: open, staleTime: Infinity });
  const reset = useMutation({
    mutationFn: profileApi.resetCalendar,
    onSuccess: (link) => {
      qc.setQueryData(['me', 'calendar'], link);
      setAsking(false);
      setCopied(null);
    },
    meta: { inline: true },
  });
  const link = q.data;
  const copy = async (kind: 'https' | 'webcal', text: string) => {
    await navigator.clipboard.writeText(text).catch(() => undefined);
    setCopied(kind);
  };

  return (
    <>
      <button type="button" className="btn-secondary h-8 gap-1.5 px-3 text-xs" onClick={() => setOpen(true)}>
        <CalendarSync className="size-3.5" /> Подписаться на календарь
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Подписка на календарь">
        <div className="space-y-4 p-5 text-sm">
          <p className="text-muted">Все ваши встречи появятся в календаре и будут обновляться сами. Ссылка личная: не передавайте её другим.</p>
          {q.isError && (
            <p role="alert" className="text-overdue">
              Не удалось получить ссылку. Попробуйте ещё раз.
            </p>
          )}
          {!link && !q.isError && <p className="text-muted">Готовим ссылку…</p>}
          {link && (
            <div className="space-y-3">
              {(
                [
                  ['webcal', 'Ссылка webcal://', link.webcalUrl],
                  ['https', 'Ссылка https://', link.url],
                ] as const
              ).map(([kind, label, value]) => (
                <div key={kind}>
                  <label className="mb-1 block text-xs text-muted" htmlFor={`cal-${kind}`}>
                    {label}
                  </label>
                  <div className="flex gap-2">
                    <input id={`cal-${kind}`} className="field min-w-0 flex-1 font-mono text-xs" readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
                    <button type="button" className="btn-secondary shrink-0" aria-label={`Скопировать ${label}`} onClick={() => copy(kind, value)}>
                      <Copy className="size-4" /> {copied === kind ? 'Скопировано' : 'Копировать'}
                    </button>
                  </div>
                </div>
              ))}
              <a className="btn-primary" href={link.webcalUrl}>
                Открыть в календаре
              </a>
            </div>
          )}
          <ul className="space-y-2">
            {STEPS.map(([app, how]) => (
              <li key={app}>
                <b>{app}.</b> <span className="text-muted">{how}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-3 border-t border-line pt-3">
            <span className="text-xs text-muted">Если ссылка попала не туда, создайте новую: старая перестанет работать.</span>
            <button type="button" className="btn-ghost shrink-0 text-xs" disabled={!link} onClick={() => setAsking(true)}>
              Сбросить ссылку
            </button>
          </div>
        </div>
      </Dialog>
      <ConfirmDialog open={asking} onClose={() => setAsking(false)} title="Сбросить ссылку?" confirmLabel="Сбросить" pending={reset.isPending} onConfirm={() => reset.mutate()}>
        Календари, подписанные по старой ссылке, перестанут обновляться. Подпишитесь заново по новой.
        {reset.error && (
          <span role="alert" className="mt-2 block text-overdue">
            Не получилось сбросить ссылку. Попробуйте ещё раз.
          </span>
        )}
      </ConfirmDialog>
    </>
  );
}
